import { Box, Flex } from '@chakra-ui/react';
import { IconRenderData, useIconData } from 'hooks/useIcon';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import { LinkWrapper } from 'modules/common/components/Link';
import { ReactElement, useMemo } from 'react';
import { useSWRConfig } from 'swr';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { Icon } from './Icon';
import { DEFAULT_GRID_GAP_PX, DEFAULT_GRID_ITEM_SIZE_PX, DEFAULT_NR_ICONS } from './IconWidget.config';
import { getIconGridArr, getIconGridId } from './IconWidget.helpers';
import { FillDirection, IconWidgetData, IconWidgetType } from './IconWidget.types';
import { IconWidgetToolbarMenu } from './IconWidgetToolbar';
import { useIconResize } from './useIconResize';

export const IconWidget = (): ReactElement => {
  const { customOnResize, customOnResizeStart, customOnResizeEnd } = useIconResize();
  const { openReplaceWidgetMenu } = useEditor();
  const { cache } = useSWRConfig();
  const { iconId, iconMetadata, type = IconWidgetType.Single } = useWidget<IconWidgetData>();

  const onDoubleClick = () => openReplaceWidgetMenu(ReplaceMenuTabIndex.ICON);

  const multiEditConfig = {
    // only mono color icons has the multi select toolbar
    displayMultiSelect: () => {
      if (type !== IconWidgetType.Single) return false;

      // Icons8 icon data are stored directly in the widget
      if (iconMetadata) {
        return iconMetadata.isMono;
      }

      const iconCacheData = cache.get(iconId)?.data;
      if (!iconCacheData) return false;

      const isMonoIcon = iconCacheData && typeof iconCacheData.color === 'number' && !iconCacheData.color;
      return isMonoIcon;
    },
    color: { key: 'shapeColorOne' },
  };

  return (
    <WidgetBase
      onDoubleClick={onDoubleClick}
      onResize={customOnResize}
      onResizeStart={customOnResizeStart}
      onResizeEnd={customOnResizeEnd}
    >
      <WidgetToolbar multiEditConfig={multiEditConfig}>
        <IconWidgetToolbarMenu />
      </WidgetToolbar>
      <IconWidgetBase />
    </WidgetBase>
  );
};

// in case the icon has partial fill the link should be rendered on top of the icon to ensure the link is clickable
const hyperLinkStyle = { position: 'absolute' as 'absolute', width: '100%', height: '100%' };

export const ReadOnlyIconWidget = ({ includeAltTextImg }: WidgetBaseProp): ReactElement => {
  const { hyperlink, isDecorative, renderType } = useWidget<IconWidgetData>();

  // Only wrap the widget with a link if its rendered with the structure tree list renderer
  // If the image is marked as decorative - do not wrap it with a link
  const isTreeRenderer =
    renderType === ReadOnlyRenderType.StructrueTreeList || renderType === ReadOnlyRenderType.ExportStructureTreeList;
  const hasHyperlink = isTreeRenderer && !isDecorative;

  return (
    <ReadOnlyWidgetBase>
      <LinkWrapper hyperlink={hasHyperlink ? hyperlink : undefined} style={hyperLinkStyle}>
        <IconWidgetBase isReadOnly />
        {includeAltTextImg && <TransparentImg />}
      </LinkWrapper>
    </ReadOnlyWidgetBase>
  );
};

const IconWidgetBase = ({ isReadOnly = false }: { isReadOnly?: boolean }) => {
  const { iconId, type = IconWidgetType.Single, embeddedSVG, iconMetadata } = useWidget<IconWidgetData>();
  // Cannot be called conditionally, as this is a hook... :(
  // if `embeddedSVG` is present, data from useIcon will be replaced.
  let iconData: IconRenderData | null = useIconData({ iconId, iconMetadata });

  if (embeddedSVG?.svg) {
    // Check of the widget contains SVG data embedded already, if exists, override
    // Leave all other properties empty, force SVG to render with default configuration.
    iconData = {
      svg: embeddedSVG.svg,
      // Set to color, to prevent color changing
      isMono: false,
      permission: 'default',
      isOldIcon: false,
    };
  }

  return (
    <>
      {iconData && type === IconWidgetType.Grid && <IconGrid isReadOnly={isReadOnly} iconData={iconData} />}
      {iconData && type === IconWidgetType.Single && <SingleIcon iconData={iconData} />}
      {/*Not sure why this raw error message string is displayed to the user, choosing to render nothing if SVG is not available*/}
      {/*TODO: Show something more sensible? - perhaps ? icon to indicate icon is not found*/}
    </>
  );
};

const IconGrid = ({ iconData, isReadOnly }: { iconData: IconRenderData; isReadOnly: boolean }) => {
  const {
    shapeColorOne,
    shapeColorTwo,
    shapeFill,
    gridGapPx = DEFAULT_GRID_GAP_PX,
    fillDirection = FillDirection.LeftRight,
    gridItemWidthPx = DEFAULT_GRID_ITEM_SIZE_PX,
    gridItemHeightPx = DEFAULT_GRID_ITEM_SIZE_PX,
    numberOfIcons = DEFAULT_NR_ICONS,
    widgetId,
    embeddedSVG,
  } = useWidget<IconWidgetData>();

  const iconGridArr = useMemo(
    () => getIconGridArr(numberOfIcons, shapeFill, shapeColorOne, shapeColorTwo),
    [numberOfIcons, shapeFill, shapeColorOne, shapeColorTwo],
  );

  const iconStyle = { width: gridItemWidthPx + 'px', height: gridItemHeightPx + 'px' };
  const flexStyle = { gap: gridGapPx + 'px' };
  const iconGridId = isReadOnly ? '' : getIconGridId(widgetId);

  return (
    <Flex id={iconGridId} wrap='wrap' style={flexStyle}>
      {iconGridArr.map((gridIcon, i) => {
        const key = `${widgetId}-grid-${i}`;
        const { shapeColorOne, shapeColorTwo, shapeFill } = gridIcon;
        const iconConfig = { shapeColorOne, shapeColorTwo, shapeFill, fillDirection };

        return (
          <Box key={key} style={iconStyle} position='relative'>
            <Icon iconData={iconData} iconConfig={iconConfig} skipPreProcessSvg={!!embeddedSVG} />
          </Box>
        );
      })}
    </Flex>
  );
};

const SingleIcon = ({ iconData }: { iconData: IconRenderData }): ReactElement => {
  const {
    shapeColorOne,
    shapeColorTwo,
    shapeFill,
    isMirrored,
    fillDirection = FillDirection.TopDown,
    embeddedSVG,
  } = useWidget<IconWidgetData>();

  return (
    <Icon
      iconConfig={{ shapeColorOne, shapeColorTwo, shapeFill, isMirrored, fillDirection }}
      iconData={iconData}
      skipPreProcessSvg={!!embeddedSVG}
    />
  );
};
